import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import {
  ArrowRight,
  Clock,
  Newspaper,
  Users,
  AlertTriangle,
  BarChart3,
  CheckCircle,
  Bell,
  Target,
  Globe,
  Zap,
  FileText,
  Award,
  Shield,
  Eye,
  Download,
  Search,
  TrendingUp,
  Mic,
  Camera,
  Play,
} from "lucide-react";
import { useEffect } from "react";

const MediaJournalismLanding = () => {
  useEffect(() => {
    document.title =
      "NationCard™ for Media & Journalism - Real-Time Public Sentiment for Credible Reporting";
  }, []);

  const challenges = [
    {
      icon: Clock,
      title: "Tight deadlines with limited opinion data",
      description:
        "Racing against time to find credible sources and representative views",
    },
    {
      icon: AlertTriangle,
      title: "Risk of amplifying unrepresentative social chatter",
      description: "Social media doesn't always reflect true public sentiment",
    },
    {
      icon: Search,
      title: "Need for quick, credible sources to back stories",
      description:
        "Struggling to find verified data that supports your narrative",
    },
    {
      icon: BarChart3,
      title: "Difficulty visualizing data for publication",
      description:
        "Time-consuming process to create publication-ready charts and graphics",
    },
  ];

  const features = [
    {
      icon: BarChart3,
      title: "Aggregate Sentiment Dashboards",
      description:
        "Access verified, representative public-opinion metrics at a glance.",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: FileText,
      title: "Embeddable Charts & Widgets",
      description:
        "Drag-and-drop ready graphics for your articles and broadcasts.",
      gradient: "from-purple-500 to-pink-500",
    },
    {
      icon: Bell,
      title: "Real-Time Alerts",
      description:
        "Get notified when major sentiment shifts occur on breaking issues.",
      gradient: "from-orange-500 to-red-500",
    },
  ];

  const benefits = [
    "Downloadable data tables for deep-dive analysis",
    "Customizable chart themes to match your brand",
    "Verified respondent panels guard against bots",
    "API access for newsroom data pipelines",
  ];

  const newsOutlets = [
    "Reuters",
    "Associated Press",
    "BBC News",
    "The Guardian",
    "Washington Post",
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden bg-gradient-to-br from-[#CC0000] via-nation-blue-dark/85 to-[#591449]">
        {/* Background overlay with hero image */}
        <div className="absolute inset-0 opacity-20 bg-[url('/img/nationcard-hero5.png')] bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#CC0000]/90 via-nation-blue-dark/85 to-[#591449]/90"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="mb-6 inline-flex items-center">
                  <Badge
                    variant="outline"
                    className="bg-white/10 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm"
                  >
                    <Newspaper size={16} className="mr-2" />
                    <span>Trusted by Leading News Organizations</span>
                  </Badge>
                </div>

                <div className="space-y-8">
                  <div className="space-y-6">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                      NationCard™ for{" "}
                      <span className="text-cyan-300">Media</span> &{" "}
                      <span className="text-blue-300">Journalism</span>
                    </h1>
                    <p className="text-xl md:text-2xl opacity-90 leading-relaxed">
                      Source, visualize, and cite real-time public sentiment for
                      richer, more credible stories.
                    </p>
                    <p className="text-lg md:text-xl opacity-80 leading-relaxed text-cyan-300">
                      Transform your reporting with verified data that tells the
                      complete story
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      size="lg"
                      className="text-lg px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-slate-900 h-auto font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                      onClick={() =>
                        (window.location.href = "/solutions/media-journalism")
                      }
                    >
                      Get Started
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white/10 hover:text-white h-auto backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                    >
                      View Sample Data
                    </Button>
                  </div>

                  {/* Trust Indicators */}
                  <div className="flex flex-wrap items-center gap-4 text-sm opacity-80">
                    <div className="flex items-center gap-2">
                      <Globe size={16} />
                      <span>Global Coverage</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield size={16} />
                      <span>Verified Sources</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      <span>Real-Time Updates</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block relative">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
                  <div className="space-y-4">
                    {/* Mock Dashboard Interface */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
                          <BarChart3 size={16} className="text-white" />
                        </div>
                        <div>
                          <div className="text-white font-semibold">
                            Sentiment Dashboard
                          </div>
                          <div className="text-white/60 text-sm">
                            Climate Policy Coverage
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                        <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse delay-150"></div>
                        <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse delay-300"></div>
                      </div>
                    </div>

                    {/* Mock Chart */}
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="text-white/80 text-xs mb-2">
                        Public Sentiment: Climate Action
                      </div>
                      <div className="flex items-end gap-2 h-16">
                        <div className="bg-green-400 rounded w-4 h-8"></div>
                        <div className="bg-blue-400 rounded w-4 h-12"></div>
                        <div className="bg-cyan-400 rounded w-4 h-16"></div>
                        <div className="bg-indigo-400 rounded w-4 h-10"></div>
                        <div className="bg-purple-400 rounded w-4 h-14"></div>
                      </div>
                      <div className="text-white/60 text-xs mt-2">
                        67% Support • 23% Neutral • 10% Oppose
                      </div>
                    </div>

                    {/* Mock Embeddable Widget */}
                    <div className="space-y-2">
                      <div className="text-white/80 text-xs">
                        Ready to Embed
                      </div>
                      <div className="bg-white/5 rounded p-2 border border-cyan-400/30">
                        <div className="text-cyan-400 text-xs font-mono">
                          &lt;iframe src="..."&gt;
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating notification */}
                <div className="absolute -top-4 -right-4 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-white text-xs">Live Update</div>
                  <div className="text-cyan-300 font-bold text-sm">
                    +2.3% shift
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Challenges Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Your Challenges
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                The modern newsroom faces unprecedented demands for speed,
                accuracy, and credibility
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {challenges.map((challenge, index) => (
                <Card
                  key={index}
                  className="hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 bg-gradient-to-br from-red-100 to-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <challenge.icon className="h-6 w-6 text-red-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2 text-foreground">
                          {challenge.title}
                        </h3>
                        <p className="text-muted-foreground">
                          {challenge.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How We Empower Your Reporting Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                How We Empower Your Reporting
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Turn complex data into compelling stories with tools built for
                modern journalism
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card
                  key={index}
                  className="group hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  <CardHeader className="pb-4">
                    <div
                      className={`h-16 w-16 bg-gradient-to-br ${feature.gradient} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                    >
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl text-foreground">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
                Built for Newsrooms
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 bg-card p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-foreground font-medium">
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Spotlight Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
                Story Spotlight
              </h2>
            </div>

            <Card className="overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="bg-gradient-to-r from-[#CC0000]/90 to-[#591449]/90 p-8">
                <div className="text-white">
                  <h3 className="text-2xl font-bold mb-4">
                    How The Daily Ledger Enhanced Coverage with NationCard™
                  </h3>
                  <p className="text-blue-100 mb-6">
                    Leading financial publication transforms election coverage
                    with real-time sentiment data
                  </p>
                </div>
              </div>
              <CardContent className="p-8">
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      20
                    </div>
                    <div className="text-muted-foreground">
                      Articles with embedded live sentiment charts
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-cyan-600 mb-2">
                      60%
                    </div>
                    <div className="text-muted-foreground">
                      Reduction in research time
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-indigo-600 mb-2">
                      300%
                    </div>
                    <div className="text-muted-foreground">
                      Increase in reader engagement
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <Button variant="outline" className="hover:bg-blue-50">
                    Read Full Case Study
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-8">
                Trusted by Leading News Organizations
              </h2>

              {/* Logo Strip */}
              <div className="flex flex-wrap justify-center items-center gap-8 opacity-60 mb-12">
                {newsOutlets.map((outlet, index) => (
                  <div
                    key={index}
                    className="text-lg font-semibold text-muted-foreground"
                  >
                    {outlet}
                  </div>
                ))}
              </div>

              {/* Testimonial */}
              <Card className="max-w-4xl mx-auto">
                <CardContent className="p-8 text-center">
                  <blockquote className="text-xl italic text-foreground mb-6">
                    "NationCard™ has revolutionized how we approach data-driven
                    journalism. The real-time sentiment insights have made our
                    election coverage more comprehensive and credible than ever
                    before."
                  </blockquote>
                  <div className="flex items-center justify-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                      <Newspaper className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-foreground">
                        Sarah Chen
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Senior Political Reporter, The Daily Ledger
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#CC0000]/90 to-[#591449]/90">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Elevate Your Reporting with NationCard™
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join leading news organizations using verified sentiment data to
              create more impactful, credible stories that resonate with
              readers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 py-4 bg-cyan-500 text-slate-900 hover:bg-cyan-400 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
                onClick={() => (window.location.href = "/contact")}
              >
                Start Your Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 border-white text-white hover:bg-white/10 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Schedule a Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default MediaJournalismLanding;
